<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import type { SettingsNavigationConfig } from './settings-layout-types';

import { AppSelect } from '../select';
import { AppStatusChip } from '../status-chip';

const props = defineProps<{ navigation: SettingsNavigationConfig }>();
const emit = defineEmits<{ select: [id: string] }>();
const router = useRouter();
const mobileSelection = ref(props.navigation.activeId);
const options = computed(() => props.navigation.items.map(({ id, label }) => ({ value: id, label })));
const activeLabel = computed(() => props.navigation.items.find((item) => item.id === props.navigation.activeId)?.label);

watch(
  () => props.navigation.activeId,
  (id) => {
    mobileSelection.value = id;
  },
);

async function selectMobile(value: unknown) {
  const item = props.navigation.items.find((item) => item.id === value);
  // AppSelect хранит выбор локально: возвращаем фактический пункт до решения route guard.
  await nextTick();
  mobileSelection.value = props.navigation.activeId;
  if (!item) return;
  if (item.to) {
    await router.push(item.to);
  } else {
    emit('select', item.id);
  }
}
</script>

<template>
  <nav
    :aria-label="navigation.title"
    class="min-w-0 xl:sticky xl:top-4"
  >
    <div class="xl:hidden">
      <AppSelect
        v-model="mobileSelection"
        :label="navigation.title"
        :options="options"
        :clearable="false"
        size="lg"
        @update:model-value="selectMobile"
      >
        <template #selected>{{ activeLabel }}</template>
      </AppSelect>
    </div>
    <div class="hidden rounded-2xl border border-(--border) p-5 xl:block">
      <div class="flex flex-col items-start gap-2 px-4 pt-2 pb-6">
        <h2 class="text-2xl leading-tight font-semibold break-words">{{ navigation.title }}</h2>
        <AppStatusChip
          v-if="navigation.statusLabel"
          tone="neutral"
        >
          <span class="sr-only">Статус: </span>{{ navigation.statusLabel }}
        </AppStatusChip>
      </div>
      <div class="flex flex-col gap-1">
        <component
          :is="item.to ? RouterLink : 'button'"
          v-for="item in navigation.items"
          :key="item.id"
          :to="item.to"
          :type="item.to ? undefined : 'button'"
          :aria-current="navigation.activeId === item.id ? 'page' : undefined"
          class="flex min-h-13 w-full cursor-pointer items-center rounded-xl px-4 py-3 text-left text-base break-words transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent)"
          :class="
            navigation.activeId === item.id
              ? 'bg-(--surface-selected) font-medium text-(--text-primary)'
              : 'text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary)'
          "
          @click="!item.to && emit('select', item.id)"
          >{{ item.label }}</component
        >
      </div>
    </div>
  </nav>
</template>
